// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef } from "react";
import { CandlestickDataPoint, DrawnLine } from "../types/chart-types";
import type { MaPoint } from "../utils/indicators";
import type { OverlayIndicatorData } from "../types/indicator-types";
import { colorWithOpacity } from "../types/indicator-types";
import {
  calculateNiceInterval,
  calculateNicePriceInterval,
  calculatePaddedPriceRange,
  ctxRoundRect,
  drawSvgFlag,
  easeOutBack,
  easeOutQuart,
  formatPriceValue,
  formatTime,
  isSameDayInTz,
  snapBetToCandle,
  tsToChartTime,
} from "../utils/chart-utils";
import { CrosshairPosition, drawCrosshair } from "../utils/crosshair-util";
import { CHART_CONFIG } from "../utils/chart-config";
import {
  getZoomedOutCandleStyle,
  sampleCandleTransition,
  type CandleShape,
  type CandleTransition,
} from "../utils/candle-rendering";
import { drawLines } from "../utils/draw-lines";
import { drawBetResultPill, type BetResultPillData } from "../utils/draw-bet-result-pill";
import { resolveEmptySpaceEdge } from "../utils/empty-space";
import { RealBetEvent } from "@/features/common/socket/types";
import { MA_COLORS } from "../utils/chart-utils";
import { drawAdvancedOverlays } from "../utils/draw-advanced-overlays";
import { getServerNowMs } from "@/shared/utils/serverTime";
import { clampToMonotonicRemainingMs } from "@/shared/utils/monotonicCountdown";

export interface BetBadgeInfo {
  id: string;
  createdAt?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  bgColor: string;
  isCall: boolean;
  amount: string;
  timeText: string;
  isCopyTrade?: boolean;
}

interface CandlestickChartProps {
  data: CandlestickDataPoint[];
  currentViewportTime: number;
  getViewportTime?: () => number;
  windowSize: number;
  isMobile: boolean;
  scrollOffset: number;
  scrollOffsetRef?: React.RefObject<number>;
  baseTimeRef: React.RefObject<number>;
  flagTimeRef: React.RefObject<number>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  overlayCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  crosshairPosition: CrosshairPosition | null;
  hoveredButton: "call" | "put" | null;
  activeBets?: RealBetEvent[];
  timeInterval?: string;
  candleDurationSeconds?: number;
  selectedTimePeriodSeconds?: number;
  drawnLines?: DrawnLine[];
  chartRefreshKey: number;
  yScaleFactor?: number;
  maDataSets?: MaPoint[][];
  overlayIndicators?: OverlayIndicatorData;
  betBadgesRef?: React.MutableRefObject<BetBadgeInfo[]>;
  lastTickPositionRef?: React.MutableRefObject<{ x: number; y: number } | null>;
  betResultRef?: React.MutableRefObject<BetResultPillData[]>;
  betResultRenderKey?: number;
  isDraggingRef?: React.RefObject<boolean>;
  renderMode?: "candlestick" | "heikin-ashi" | "bars";
  timezone?: string;
  pricePrecision?: number;
  verticalBias?: number;
  smoothedMinPriceRef?: React.MutableRefObject<number | null>;
  smoothedPriceRangeRef?: React.MutableRefObject<number | null>;
  pairRevealStartRef?: React.RefObject<number>;
}

export const CandlestickChart = ({
  data,
  currentViewportTime: currentViewportTimeProp,
  getViewportTime,
  windowSize,
  isMobile,
  scrollOffset,
  scrollOffsetRef,
  baseTimeRef,
  flagTimeRef,
  canvasRef,
  overlayCanvasRef,
  crosshairPosition,
  hoveredButton,
  activeBets = [],
  timeInterval,
  candleDurationSeconds,
  selectedTimePeriodSeconds,
  drawnLines = [],
  chartRefreshKey,
  yScaleFactor = 1,
  maDataSets = [],
  overlayIndicators = {},
  betBadgesRef,
  lastTickPositionRef,
  betResultRef,
  betResultRenderKey,
  isDraggingRef,
  renderMode = "candlestick",
  timezone,
  pricePrecision,
  verticalBias = 0,
  smoothedMinPriceRef: smoothedMinPriceRefProp,
  smoothedPriceRangeRef: smoothedPriceRangeRefProp,
  pairRevealStartRef,
}: CandlestickChartProps) => {
  const timeIntervalRef = useRef(timeInterval);
  timeIntervalRef.current = timeInterval;
  const canvasSizeRef = useRef({ w: 0, h: 0 });
  const overlaySizeRef = useRef({ w: 0, h: 0 });
  // Frozen lastDataTime during drag — prevents emptySpace/X-axis shift when new candles arrive
  const frozenLastDataTimeRef = useRef(0);
  // Tracks whether the previous frame was rendered in live (non-dragging, scrollOffset 0)
  // mode, so we can detect the live → drag/scroll transition and freeze emptySpace cleanly.
  const wasLiveRef = useRef(true);

  // Animation refs for smooth last-candle tick updates
  const liveCandleTransitionRef = useRef<CandleTransition | null>(null);
  const displayedLastCandleRef = useRef<CandleShape | null>(null);
  const prevLastCandleValRef = useRef<{ close: number; high: number; low: number } | null>(null);

  const prevLastCandleTimeRef = useRef<number | null>(null);
  const candleBirthTimeRef = useRef<number>(-1);

  const localSmoothedMinPriceRef = useRef<number | null>(null);
  const localSmoothedPriceRangeRef = useRef<number | null>(null);
  const smoothedMinPriceRef = smoothedMinPriceRefProp ?? localSmoothedMinPriceRef;
  const smoothedPriceRangeRef = smoothedPriceRangeRefProp ?? localSmoothedPriceRangeRef;
  const overlayIndicatorsRef = useRef(overlayIndicators);
  overlayIndicatorsRef.current = overlayIndicators;
  const maDataSetsRef = useRef(maDataSets);
  maDataSetsRef.current = maDataSets;
  const overlayScaleKeyRef = useRef<string>("");

  const betAnchorRef = useRef<
    Map<string, { startTime: number; targetTime: number; isLive: boolean; entryPrice: number; birthTime: number }>
  >(new Map());
  const betBirthTimesRef = useRef<Map<string, number>>(new Map());
  const currentPriceBadgeRef = useRef<{ price: number; y: number; color: string } | null>(null);

  // Clear cached bet anchors when timeframe changes — anchors store chart-time coords
  // calculated with the old msPerCandle, so they'd map to wrong candles on a new timeframe.
  useEffect(() => {
    betAnchorRef.current.clear();
  }, [candleDurationSeconds]);

  useEffect(() => {
    betAnchorRef.current.clear();
  }, [chartRefreshKey]);

  useEffect(() => {
    liveCandleTransitionRef.current = null;
    displayedLastCandleRef.current = null;
    prevLastCandleValRef.current = null;
    prevLastCandleTimeRef.current = null;
    candleBirthTimeRef.current = -1;
  }, [chartRefreshKey, candleDurationSeconds, renderMode]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        betAnchorRef.current.clear();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Heikin Ashi: compute transformed data whenever source data changes
  const haData = useMemo(() => {
    if (renderMode !== "heikin-ashi" || data.length === 0) return data;
    const result: CandlestickDataPoint[] = [];
    for (let i = 0; i < data.length; i++) {
      const c = data[i];
      const haClose = (c.open + c.high + c.low + c.close) / 4;
      const haOpen = i === 0 ? (c.open + c.close) / 2 : (result[i - 1].open + result[i - 1].close) / 2;
      const haHigh = Math.max(c.high, haOpen, haClose);
      const haLow = Math.min(c.low, haOpen, haClose);
      result.push({ ...c, open: haOpen, high: haHigh, low: haLow, close: haClose });
    }
    return result;
  }, [data, renderMode]);

  const getVisibleRange = useCallback(() => {
    const vt = getViewportTime ? getViewportTime() : currentViewportTimeProp;
    const rightOffsetSpan = windowSize * CHART_CONFIG.padding.emptySpaceFraction;
    const startTime = vt - (windowSize - rightOffsetSpan);
    const drawEndTime = vt + rightOffsetSpan + 1;

    // Binary search for startIndex (first point with time >= startTime)
    let lo = 0,
      hi = data.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (data[mid].time < startTime) lo = mid + 1;
      else hi = mid;
    }
    const startIndex = lo;

    // Binary search for endIndex (first point with time > drawEndTime)
    lo = startIndex;
    hi = data.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (data[mid].time <= drawEndTime) lo = mid + 1;
      else hi = mid;
    }
    const endIndex = lo;

    return { startIndex, endIndex };
  }, [data, currentViewportTimeProp, windowSize]);

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // When data is cleared (pair switch / history reload) reset all per-pair animation
    // state so the next pair's first frame starts clean.  Without this,
    // prevLastCandleTimeRef keeps the old pair's time index; if the new pair loads the
    // same number of candles (same last-time index) the chart triggers a price-transition
    // animation between the two pairs' close prices, producing a tall spike on the live
    // candle for the duration of the transition.
    if (data.length === 0) {
      liveCandleTransitionRef.current = null;
      displayedLastCandleRef.current = null;
      prevLastCandleValRef.current = null;
      prevLastCandleTimeRef.current = null;
      candleBirthTimeRef.current = -1;
      smoothedMinPriceRef.current = null;
      smoothedPriceRangeRef.current = null;
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const currentViewportTime = getViewportTime ? getViewportTime() : currentViewportTimeProp;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const newW = Math.round(rect.width * dpr);
    const newH = Math.round(rect.height * dpr);
    if (canvasSizeRef.current.w !== newW || canvasSizeRef.current.h !== newH) {
      canvas.width = newW;
      canvas.height = newH;
      canvasSizeRef.current = { w: newW, h: newH };
      ctx.scale(dpr, dpr);
    }

    const width = rect.width;
    const height = rect.height;

    const rawLastDataTime = data.length > 0 ? data[data.length - 1].time : 0;
    // Read scroll state from the ref (matches getViewportTime) so the empty-space edge stays in
    // lockstep with the indicator sub-panels, which also read the ref. The prop lags by a frame.
    const effectiveScrollOffset = scrollOffsetRef?.current ?? scrollOffset;
    const isDragging = isDraggingRef?.current ?? false;
    // Shared edge resolution — keeps candles and every indicator sub-panel on the same time axis.
    const lastDataTime = resolveEmptySpaceEdge({
      isDragging,
      scrollOffset: effectiveScrollOffset,
      rawLastDataTime,
      currentViewportTime,
      frozenRef: frozenLastDataTimeRef,
      wasLiveRef,
    });

    const rightPadding = isMobile ? 50 : 95;
    const maxEmptySpace = Math.round((width - rightPadding) * CHART_CONFIG.padding.emptySpaceFraction);
    const transitionDuration = 5;
    let emptySpace: number;

    if (effectiveScrollOffset === 0) {
      emptySpace = maxEmptySpace;
    } else {
      if (currentViewportTime >= lastDataTime) {
        emptySpace = maxEmptySpace;
      } else if (currentViewportTime >= lastDataTime - transitionDuration) {
        const distanceFromCurrent = lastDataTime - currentViewportTime;
        const progress = distanceFromCurrent / transitionDuration;
        emptySpace = maxEmptySpace * (1 - progress);
      } else {
        emptySpace = 0;
      }
    }

    const baseChartWidth = width - rightPadding - emptySpace;

    // stableDrawWidth is used for all coordinate calculations so that the scale
    // (pixels per time unit) stays constant during panning. The live-mode right-side
    // empty space is achieved by shifting viewportStartTime, not by shrinking drawWidth.
    const stableDrawWidth = width - rightPadding;
    const emptySpaceFraction = stableDrawWidth > 0 ? emptySpace / stableDrawWidth : 0;

    const bottomPadding = isMobile ? 16 : 30;
    const chartHeight = height - bottomPadding;

    ctx.clearRect(0, 0, width, height);

    const { startIndex, endIndex } = getVisibleRange();
    // For Heikin Ashi use pre-computed HA values; otherwise use raw data
    const sourceData = renderMode === "heikin-ashi" ? haData : data;
    const visibleData = sourceData.slice(startIndex, endIndex);

    // Smooth animation for the live (last) candle on each tick
    // Always reference the last entry from sourceData so identity check works for HA too
    const lastDataCandle = sourceData[sourceData.length - 1];
    // Track raw data for change detection (HA values are derived, raw ticks drive the change)
    const rawLastCandle = data[data.length - 1];
    const animationNow = performance.now();

    // Pair-switch stagger reveal: candles appear one by one left→right
    const PAIR_STAGGER_MS = 600; // window over which all candles start
    const PAIR_GROW_MS = 260; // how long each individual candle grows
    const PAIR_REVEAL_DURATION = PAIR_STAGGER_MS + PAIR_GROW_MS; // 860ms total
    const pairRevealElapsed = pairRevealStartRef?.current
      ? Math.max(0, animationNow - pairRevealStartRef.current)
      : PAIR_REVEAL_DURATION;
    const isRevealing = pairRevealElapsed < PAIR_REVEAL_DURATION;

    const targetCandleShape: CandleShape = {
      close: lastDataCandle.close,
      high: lastDataCandle.high,
      low: lastDataCandle.low,
    };
    const prevRawCandle = prevLastCandleValRef.current;
    const previousTime = prevLastCandleTimeRef.current;
    const lastTime = rawLastCandle?.time;
    const isNewCandle =
      previousTime !== null && typeof lastTime === "number" && lastTime > previousTime && lastTime - previousTime <= 3;
    const shouldResetAnimation =
      previousTime === null || typeof lastTime !== "number" || lastTime < previousTime || lastTime - previousTime > 3;
    const candleChanged =
      prevRawCandle !== null &&
      (prevRawCandle.close !== rawLastCandle.close ||
        prevRawCandle.high !== rawLastCandle.high ||
        prevRawCandle.low !== rawLastCandle.low);

    if (isNewCandle) candleBirthTimeRef.current = animationNow;

    if (shouldResetAnimation || prevRawCandle === null || isNewCandle) {
      liveCandleTransitionRef.current = null;
      displayedLastCandleRef.current = targetCandleShape;
    } else if (candleChanged) {
      liveCandleTransitionRef.current = {
        from: displayedLastCandleRef.current ?? prevRawCandle,
        to: targetCandleShape,
        startTime: animationNow,
        duration: CHART_CONFIG.animation.liveCandleDuration,
      };
    }

    prevLastCandleValRef.current = {
      close: rawLastCandle.close,
      high: rawLastCandle.high,
      low: rawLastCandle.low,
    };
    if (typeof lastTime === "number") prevLastCandleTimeRef.current = lastTime;

    const candleGrowElapsed = performance.now() - candleBirthTimeRef.current;
    const candleGrowProgress = Math.min(candleGrowElapsed / 220, 1);
    const candleGrowEased = candleGrowProgress >= 1 ? 1 : easeOutQuart(candleGrowProgress);

    let animatedCandleShape = targetCandleShape;
    const liveTransition = liveCandleTransitionRef.current;
    if (liveTransition !== null) {
      const sample = sampleCandleTransition(liveTransition, animationNow);
      animatedCandleShape = sample.shape;
      if (sample.done) liveCandleTransitionRef.current = null;
    }
    displayedLastCandleRef.current = animatedCandleShape;
    const animClose = animatedCandleShape.close;
    const animHigh = animatedCandleShape.high;
    const animLow = animatedCandleShape.low;

    // Calculate dynamic price range from visible data with 10% padding.
    // Include overlay channels (BB / Envelopes / Keltner / Donchian) so bands stay on-screen.
    const overlayNow = overlayIndicatorsRef.current;
    const maNow = maDataSetsRef.current;
    const overlayRangeExtras: Array<{ high: number; low: number }> = [];
    {
      const t0 = visibleData[0]?.time;
      const t1 = visibleData[visibleData.length - 1]?.time;
      if (typeof t0 === "number" && typeof t1 === "number") {
        const { bb, keltner, donchian, alligator, chaosbands, ichimoku, zigzag } = overlayNow;
        // Envelopes use % of price — including them in Y-scale blows up the chart on BTC.
        // Keep scale on price (+ BB/Keltner/Donchian); env bands sit near candles at default 0.1%.
        for (const series of [bb, keltner, donchian, chaosbands]) {
          if (!series) continue;
          for (const p of series) {
            if (p.time < t0 || p.time > t1) continue;
            if ("upper" in p || "lower" in p) {
              const upper = (p as any).upper;
              const lower = (p as any).lower;
              if (Number.isFinite(upper)) overlayRangeExtras.push({ high: upper, low: upper });
              if (Number.isFinite(lower)) overlayRangeExtras.push({ high: lower, low: lower });
            }
          }
        }
        if (alligator) {
          for (const series of [alligator.jaws, alligator.teeth, alligator.lips]) {
            for (const p of series) {
              if (p.time < t0 || p.time > t1) continue;
              overlayRangeExtras.push({ high: p.value, low: p.value });
            }
          }
        }
        if (ichimoku) {
          for (const p of ichimoku) {
            if (p.time < t0 || p.time > t1) continue;
            for (const value of [p.tenkan, p.kijun, p.senkouA, p.senkouB, p.chikou]) {
              if (Number.isFinite(value)) overlayRangeExtras.push({ high: value!, low: value! });
            }
          }
        }
        if (zigzag) {
          for (const p of zigzag) {
            if (p.time >= t0 && p.time <= t1) overlayRangeExtras.push({ high: p.value, low: p.value });
          }
        }
      }
    }
    const { paddedMinPrice: targetMin, paddedPriceRange: targetRange } = calculatePaddedPriceRange({
      visibleData: overlayRangeExtras.length > 0 ? [...visibleData, ...overlayRangeExtras] : visibleData,
      chartType: "candlestick",
      verticalBias,
    });

    // Snap Y-scale immediately when overlay channels appear/change, so bands aren't stuck off-screen.
    const overlayScaleKey = [
      overlayNow.bb?.length ?? 0,
      overlayNow.envelopes?.length ?? 0,
      overlayNow.keltner?.length ?? 0,
      overlayNow.donchian?.length ?? 0,
      overlayNow.alligator?.jaws?.length ?? 0,
      overlayNow.chaosbands?.length ?? 0,
      overlayNow.ichimoku?.length ?? 0,
      overlayNow.zigzag?.length ?? 0,
      targetMin.toFixed(4),
      targetRange.toFixed(4),
    ].join("|");
    if (overlayScaleKeyRef.current !== overlayScaleKey) {
      overlayScaleKeyRef.current = overlayScaleKey;
      smoothedMinPriceRef.current = targetMin;
      smoothedPriceRangeRef.current = targetRange;
    }

    if (
      smoothedMinPriceRef.current === null ||
      smoothedPriceRangeRef.current === null ||
      Math.abs(targetMin - smoothedMinPriceRef.current) > targetRange * 0.5 ||
      Math.abs(targetRange - smoothedPriceRangeRef.current) > targetRange * 0.5
    ) {
      smoothedMinPriceRef.current = targetMin;
      smoothedPriceRangeRef.current = targetRange;
    } else {
      const k = 0.1;
      smoothedMinPriceRef.current += (targetMin - smoothedMinPriceRef.current) * k;
      smoothedPriceRangeRef.current += (targetRange - smoothedPriceRangeRef.current) * k;
    }
    const _center = smoothedMinPriceRef.current + smoothedPriceRangeRef.current / 2;
    const paddedPriceRange = smoothedPriceRangeRef.current / yScaleFactor;
    const paddedMinPrice = _center - paddedPriceRange / 2;

    // Shift viewportStartTime forward so the last candle lands at x = baseChartWidth
    // (the left edge of the empty zone), keeping the scale constant.
    const viewportStartTime = currentViewportTime - windowSize * (1 - emptySpaceFraction);
    const viewportEndTime = currentViewportTime + windowSize * emptySpaceFraction;

    const drawStartX = 0;
    const drawWidth = stableDrawWidth;

    const getX = (dataPoint: CandlestickDataPoint) => {
      const timePosition = dataPoint.time - viewportStartTime;
      const normalizedPosition = timePosition / windowSize;
      return drawStartX + normalizedPosition * drawWidth;
    };

    const getY = (price: number) => {
      return chartHeight - ((price - paddedMinPrice) / paddedPriceRange) * chartHeight;
    };

    // Draw grid aligned with price and time labels
    ctx.strokeStyle = "#374151";
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.3;

    const gridEndX = width - rightPadding;

    // Horizontal grid lines snapped to nice price intervals (extend to full width so they run under price labels)
    const priceInterval = calculateNicePriceInterval(paddedPriceRange);
    const firstGridPrice = Math.ceil(paddedMinPrice / priceInterval) * priceInterval;
    for (let price = firstGridPrice; price <= paddedMinPrice + paddedPriceRange; price += priceInterval) {
      const y = getY(price);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width - 25, y);
      ctx.stroke();
    }

    // Vertical grid lines — same interval as time labels
    {
      const gShowSeconds = windowSize <= 180;
      const gViewportTs = baseTimeRef.current + currentViewportTime * 1000;
      const gViewportDate = new Date(gViewportTs);
      const gNow = new Date();
      const gIsDifferentDay = !isSameDayInTz(gViewportDate, gNow, timezone);
      const gLabelW = gIsDifferentDay ? (gShowSeconds ? 120 : 100) : gShowSeconds ? 65 : 45;
      const gMaxLabels = Math.floor(baseChartWidth / gLabelW);
      const gActualLabels = Math.max(3, Math.min(gMaxLabels, 10));
      // Use viewportStartTime (same origin as candles) for aligned grid lines
      const gTimeRange = viewportEndTime - viewportStartTime;
      const gRawInterval = gTimeRange / (gActualLabels - 1);
      const gNiceInterval = calculateNiceInterval(gRawInterval, gShowSeconds);

      const gFirstTick = Math.floor(viewportStartTime / gNiceInterval) * gNiceInterval;

      for (let t = gFirstTick; t <= viewportStartTime + windowSize + gNiceInterval; t += gNiceInterval) {
        const x = ((t - viewportStartTime) / windowSize) * stableDrawWidth;
        if (x >= 0 && x <= gridEndX) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, chartHeight);
          ctx.stroke();
        }
      }
    }

    ctx.globalAlpha = 1;

    // Clip region for chart
    ctx.save();
    ctx.beginPath();

    const clipWidth = baseChartWidth + emptySpace;

    ctx.rect(0, 0, clipWidth, chartHeight);
    ctx.clip();

    // Draw hover zone BELOW candlesticks if button is hovered (desktop only)
    if (hoveredButton && visibleData.length >= 2 && window.innerWidth >= 770) {
      const lastCandle = visibleData[visibleData.length - 1];
      const currentPriceY = getY(lastCandle.close);
      const zoneEndX = width - rightPadding;

      let zoneStartY: number;
      let zoneHeight: number;

      if (hoveredButton === "call") {
        zoneStartY = 0;
        zoneHeight = currentPriceY;
      } else {
        zoneStartY = currentPriceY;
        zoneHeight = chartHeight - currentPriceY;
      }

      const zoneGradient = ctx.createLinearGradient(0, zoneStartY, 0, zoneStartY + zoneHeight);
      if (hoveredButton === "call") {
        zoneGradient.addColorStop(0, "rgba(23, 201, 100, 0)");
        zoneGradient.addColorStop(1, "rgba(23, 201, 100, 0.15)");
      } else {
        zoneGradient.addColorStop(0, "rgba(243, 18, 96, 0.15)");
        zoneGradient.addColorStop(1, "rgba(243, 18, 96, 0)");
      }

      ctx.fillStyle = zoneGradient;
      ctx.fillRect(0, zoneStartY, zoneEndX, zoneHeight);
    }

    // Draw candlesticks / heikin-ashi / bars only if we have enough data
    if (visibleData.length >= 2) {
      // Calculate candle width based on visible data count
      const candleSpacing = drawWidth / windowSize;
      const candleStyle = getZoomedOutCandleStyle(candleSpacing);
      const isDenseCandleView = candleSpacing < 4;
      const denseCandlePhysicalWidth = Math.max(1, Math.round(candleStyle.bodyWidth * dpr));
      const candleWidth = isDenseCandleView ? denseCandlePhysicalWidth / dpr : candleStyle.bodyWidth;
      const wickPhysicalWidth = isDenseCandleView ? denseCandlePhysicalWidth : candleStyle.wickWidth * dpr;
      const candleWickWidth = wickPhysicalWidth / dpr;
      const minBodyHeight = isDenseCandleView ? 1 / dpr : 1;

      const bullishColor = "#17C964";
      const bearishColor = "#F31260";

      const totalVisible = visibleData.length;

      if (renderMode === "bars") {
        const tickLen = Math.round(Math.min(Math.max(3, candleWidth / 2), candleSpacing / 2 - 2));
        const barLineWidth = Math.round(Math.max(2, tickLen / 3));

        visibleData.forEach((candle, i) => {
          let barEased = 1;
          if (isRevealing) {
            const staggerDelay = totalVisible > 1 ? (i / (totalVisible - 1)) * PAIR_STAGGER_MS : 0;
            const candleElapsed = pairRevealElapsed - staggerDelay;
            if (candleElapsed <= 0) return;
            barEased = easeOutQuart(Math.min(candleElapsed / PAIR_GROW_MS, 1));
          }

          const isLiveBar = candle === lastDataCandle;
          const x = Math.round(getX(candle));
          const openY = Math.round(getY(candle.open));
          let closeY = Math.round(getY(isLiveBar ? animClose : candle.close));
          let highY = Math.round(getY(isLiveBar ? animHigh : candle.high));
          let lowY = Math.round(getY(isLiveBar ? animLow : candle.low));
          if (barEased < 1) {
            closeY = Math.round(openY + (closeY - openY) * barEased);
            highY = Math.round(openY + (highY - openY) * barEased);
            lowY = Math.round(openY + (lowY - openY) * barEased);
          }
          if (isLiveBar && candleGrowProgress < 1) {
            closeY = Math.round(openY + (closeY - openY) * candleGrowEased);
            highY = Math.round(openY + (highY - openY) * candleGrowEased);
            lowY = Math.round(openY + (lowY - openY) * candleGrowEased);
          }
          const isBullish = (isLiveBar ? animClose : candle.close) >= candle.open;
          const color = isBullish ? bullishColor : bearishColor;

          ctx.strokeStyle = color;
          ctx.lineWidth = barLineWidth;
          ctx.lineCap = "square";

          ctx.beginPath();
          ctx.moveTo(x, highY);
          ctx.lineTo(x, lowY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(x - tickLen, openY);
          ctx.lineTo(x, openY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(x, closeY);
          ctx.lineTo(x + tickLen, closeY);
          ctx.stroke();
        });
      } else if (isRevealing) {
        visibleData.forEach((candle, i) => {
          const staggerDelay = totalVisible > 1 ? (i / (totalVisible - 1)) * PAIR_STAGGER_MS : 0;
          const candleElapsed = pairRevealElapsed - staggerDelay;
          if (candleElapsed <= 0) return;
          const candleEased = easeOutQuart(Math.min(candleElapsed / PAIR_GROW_MS, 1));

          const isLive = candle === lastDataCandle;
          const rawX = getX(candle);
          const wickX = isDenseCandleView
            ? (Math.floor(rawX * dpr) + (denseCandlePhysicalWidth % 2 === 1 ? 0.5 : 0)) / dpr
            : rawX;
          const openY = getY(candle.open);
          let closeY = getY(isLive ? animClose : candle.close);
          let highY = getY(isLive ? animHigh : candle.high);
          let lowY = getY(isLive ? animLow : candle.low);
          closeY = openY + (closeY - openY) * candleEased;
          highY = openY + (highY - openY) * candleEased;
          lowY = openY + (lowY - openY) * candleEased;
          if (isLive && candleGrowProgress < 1) {
            closeY = openY + (closeY - openY) * candleGrowEased;
            highY = openY + (highY - openY) * candleGrowEased;
            lowY = openY + (lowY - openY) * candleGrowEased;
          }
          if (isDenseCandleView) {
            highY = Math.round(highY * dpr) / dpr;
            lowY = Math.round(lowY * dpr) / dpr;
          }

          const candleClose = isLive ? animClose : candle.close;
          const isBullish = candleClose >= candle.open;
          const rawBodyTop = Math.min(openY, closeY);
          const rawBodyBottom = Math.max(openY, closeY);
          const bodyTop = isDenseCandleView ? Math.round(rawBodyTop * dpr) / dpr : rawBodyTop;
          const snappedBodyBottom = isDenseCandleView ? Math.round(rawBodyBottom * dpr) / dpr : rawBodyBottom;
          const bodyHeight = Math.max(snappedBodyBottom - bodyTop, minBodyHeight);
          const bodyLeft = isDenseCandleView ? wickX - candleWidth / 2 : rawX - candleWidth / 2;
          const color = isBullish ? bullishColor : bearishColor;

          ctx.lineWidth = candleWickWidth;
          ctx.strokeStyle = color;
          ctx.beginPath();
          ctx.moveTo(wickX, highY);
          ctx.lineTo(wickX, lowY);
          ctx.stroke();

          ctx.fillStyle = color;
          ctx.fillRect(bodyLeft, bodyTop, candleWidth, bodyHeight);
        });
      } else {
        ctx.save();

        const bullishWicks = new Path2D();
        const bearishWicks = new Path2D();
        const bullishBodies = new Path2D();
        const bearishBodies = new Path2D();

        visibleData.forEach((candle) => {
          const isLive = candle === lastDataCandle;
          const rawX = getX(candle);
          const wickX = isDenseCandleView
            ? (Math.floor(rawX * dpr) + (denseCandlePhysicalWidth % 2 === 1 ? 0.5 : 0)) / dpr
            : rawX;
          const openY = getY(candle.open);
          let closeY = getY(isLive ? animClose : candle.close);
          let highY = getY(isLive ? animHigh : candle.high);
          let lowY = getY(isLive ? animLow : candle.low);
          if (isLive && candleGrowProgress < 1) {
            closeY = openY + (closeY - openY) * candleGrowEased;
            highY = openY + (highY - openY) * candleGrowEased;
            lowY = openY + (lowY - openY) * candleGrowEased;
          }
          if (isDenseCandleView) {
            highY = Math.round(highY * dpr) / dpr;
            lowY = Math.round(lowY * dpr) / dpr;
          }

          const candleClose = isLive ? animClose : candle.close;
          const isBullish = candleClose >= candle.open;
          const rawBodyTop = Math.min(openY, closeY);
          const rawBodyBottom = Math.max(openY, closeY);
          const bodyTop = isDenseCandleView ? Math.round(rawBodyTop * dpr) / dpr : rawBodyTop;
          const snappedBodyBottom = isDenseCandleView ? Math.round(rawBodyBottom * dpr) / dpr : rawBodyBottom;
          const bodyHeight = Math.max(snappedBodyBottom - bodyTop, minBodyHeight);
          const rawBodyLeft = rawX - candleWidth / 2;
          const bodyLeft = isDenseCandleView ? wickX - candleWidth / 2 : rawBodyLeft;

          const wickPath = isBullish ? bullishWicks : bearishWicks;
          const bodyPath = isBullish ? bullishBodies : bearishBodies;

          wickPath.moveTo(wickX, highY);
          wickPath.lineTo(wickX, lowY);
          bodyPath.rect(bodyLeft, bodyTop, candleWidth, bodyHeight);
        });

        ctx.lineWidth = candleWickWidth;
        ctx.strokeStyle = bullishColor;
        ctx.stroke(bullishWicks);
        ctx.strokeStyle = bearishColor;
        ctx.stroke(bearishWicks);
        ctx.fillStyle = bullishColor;
        ctx.fill(bullishBodies);
        ctx.fillStyle = bearishColor;
        ctx.fill(bearishBodies);

        ctx.restore();
      }

      // Draw High/Low price labels on chart
      if (visibleData.length > 0) {
        // Find high and low points from candles
        let highCandle = visibleData[0];
        let lowCandle = visibleData[0];

        visibleData.forEach((candle) => {
          if (candle.high > highCandle.high) highCandle = candle;
          if (candle.low < lowCandle.low) lowCandle = candle;
        });

        // Draw High label
        const highX = getX(highCandle);
        const highY = getY(highCandle.high);
        const highText = formatPriceValue(highCandle.high, pricePrecision);

        ctx.font = `${isMobile ? 9 : 11}px sans-serif`;
        const highTextWidth = ctx.measureText(highText).width;

        const highLabelX = Math.min(Math.max(highX - highTextWidth / 2, 5), stableDrawWidth - highTextWidth - 5);
        const highLabelY = Math.max(highY - 18, 12);

        // Background
        ctx.fillStyle = "#33333380";
        ctx.beginPath();
        ctxRoundRect(ctx, highLabelX - 4, highLabelY - 8, highTextWidth + 8, 16, 3);
        ctx.fill();

        // Text
        ctx.fillStyle = "#737373";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(highText, highLabelX, highLabelY);

        // Draw Low label
        const lowX = getX(lowCandle);
        const lowY = getY(lowCandle.low);
        const lowText = formatPriceValue(lowCandle.low, pricePrecision);

        const lowTextWidth = ctx.measureText(lowText).width;

        const lowLabelX = Math.min(Math.max(lowX - lowTextWidth / 2, 5), stableDrawWidth - lowTextWidth - 5);
        const lowLabelY = Math.min(lowY + 18, chartHeight - 12);

        // Background
        ctx.fillStyle = "#33333380";
        ctx.beginPath();
        ctxRoundRect(ctx, lowLabelX - 4, lowLabelY - 8, lowTextWidth + 8, 16, 3);
        ctx.fill();

        // Text
        ctx.fillStyle = "#737373";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(lowText, lowLabelX, lowLabelY);
      }
    }

    // Draw current price line - white, solid, full width
    let currentPriceBadgeData: { price: number; y: number; color: string } | null = null;

    if (data.length > 0) {
      const lastCandle = data[data.length - 1];
      const currentPrice = animClose;
      const currentPriceY = getY(currentPrice);

      // Draw solid white line across full chart width
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(0, currentPriceY);
      ctx.lineTo(width - rightPadding, currentPriceY);
      ctx.stroke();
      ctx.globalAlpha = 1;

      // Store badge data to draw after ctx.restore()
      currentPriceBadgeData = { price: currentPrice, y: currentPriceY, color: "#FFFFFF" };

      // Update last tick position for bet result overlay
      if (lastTickPositionRef) {
        const lastCandleXForOverlay = getX(lastCandle);
        lastTickPositionRef.current = { x: lastCandleXForOverlay, y: currentPriceY };
      }

      // Hover zones are drawn in drawChart (below candlesticks)
    }

    // Draw timeframe label and countdown timer to the right of the last candle
    const currentTimeInterval = timeIntervalRef.current;
    if (data.length >= 2 && currentTimeInterval) {
      const lastCandle = data[data.length - 1];
      const lastCandleX = getX(lastCandle);
      const candleSpacing = drawWidth / windowSize;
      const halfCandleW = Math.max(1, candleSpacing - 2) / 2;
      const textX = lastCandleX + halfCandleW + 6;
      const clipWidth = baseChartWidth + emptySpace;

      if (textX >= 0 && textX < clipWidth - 4) {
        const candleSeconds = candleDurationSeconds ?? 60;
        const nowSeconds = Date.now() / 1000;
        const currentPeriodStart = Math.floor(nowSeconds / candleSeconds) * candleSeconds;
        const nextPeriodStart = currentPeriodStart + candleSeconds;
        const timeRemainingSeconds = Math.max(0, Math.floor(nextPeriodStart - nowSeconds));

        let timerText: string;
        if (candleSeconds >= 86400) {
          const h = Math.floor(timeRemainingSeconds / 3600);
          const m = Math.floor((timeRemainingSeconds % 3600) / 60);
          const s = timeRemainingSeconds % 60;
          timerText = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
        } else {
          const m = Math.floor(timeRemainingSeconds / 60);
          const s = timeRemainingSeconds % 60;
          timerText = `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
        }

        const candleBodyTopY = Math.min(getY(lastCandle.open), getY(lastCandle.close));
        const candleBodyBottomY = Math.max(getY(lastCandle.open), getY(lastCandle.close));
        const candleMidY = (candleBodyTopY + candleBodyBottomY) / 2;

        const fontSize = isMobile ? 9 : 11;
        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.globalAlpha = 1;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#FFFFFF";
        ctx.fillText(currentTimeInterval, textX, candleMidY - fontSize);
        ctx.fillStyle = "#737373";
        ctx.fillText(timerText, textX, candleMidY + fontSize * 0.6);
      }
    }

    // ── Overlay indicators ────────────────────────────────────────────────────
    const {
      bb,
      supertrend,
      donchian,
      sar,
      envelopes,
      keltner,
      alligator,
      fractal,
      chaosbands,
      ichimoku,
      zigzag,
      envelopesStyle,
      keltnerStyle,
      alligatorStyle,
      fractalStyle,
      chaosbandsStyle,
      ichimokuStyle,
      zigzagStyle,
      bbStyle,
      supertrendStyle,
      donchianStyle,
      sarStyle,
    } = overlayNow;
    drawAdvancedOverlays({
      ctx,
      ichimoku,
      zigzag,
      ichimokuStyle,
      zigzagStyle,
      viewportStartTime,
      viewportEndTime,
      windowSize,
      drawStartX,
      drawWidth,
      getY,
    });

    // Bollinger Bands (3 dashed lines)
    if (bb && bb.length >= 2) {
      const styles = bbStyle?.lines;
      const bbVisible = bb.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (bbVisible.length >= 2) {
        const drawBand = (pts: { x: number; y: number }[], color: string, dash: number[]) => {
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = 1;
          ctx.setLineDash(dash);
          ctx.lineJoin = "round";
          ctx.lineCap = "round";
          ctx.beginPath();
          pts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          ctx.stroke();
          ctx.restore();
        };
        const toXY = (p: any, key: string) => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]),
        });
        const fill = bbStyle?.fills?.background;
        if (fill?.visible) {
          ctx.save();
          ctx.globalAlpha = fill.opacity;
          ctx.fillStyle = fill.color;
          ctx.beginPath();
          bbVisible.forEach((p, i) => {
            const { x, y } = toXY(p, "upper");
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          [...bbVisible].reverse().forEach((p) => {
            const { x, y } = toXY(p, "lower");
            ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
        if (styles?.top?.visible !== false)
          drawBand(
            bbVisible.map((p) => toXY(p, "upper")),
            styles?.top?.color ?? "rgba(167,139,250,0.8)",
            [],
            styles?.top?.width ?? 1,
          );
        if (styles?.middle?.visible !== false)
          drawBand(
            bbVisible.map((p) => toXY(p, "middle")),
            styles?.middle?.color ?? "rgba(167,139,250,0.5)",
            [4, 4],
            styles?.middle?.width ?? 1,
          );
        if (styles?.bottom?.visible !== false)
          drawBand(
            bbVisible.map((p) => toXY(p, "lower")),
            styles?.bottom?.color ?? "rgba(167,139,250,0.8)",
            [],
            styles?.bottom?.width ?? 1,
          );
      }
    }

    // SuperTrend (color changes with trend)
    if (supertrend && supertrend.length >= 2) {
      const stVisible = supertrend.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (stVisible.length >= 2) {
        ctx.save();
        ctx.lineWidth = supertrendStyle?.lines?.up?.width ?? 2;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        for (let i = 1; i < stVisible.length; i++) {
          const p0 = stVisible[i - 1],
            p1 = stVisible[i];
          const x0 = drawStartX + ((p0.time - viewportStartTime) / windowSize) * drawWidth;
          const x1 = drawStartX + ((p1.time - viewportStartTime) / windowSize) * drawWidth;
          const trendStyle = p1.trend === 1 ? supertrendStyle?.lines?.up : supertrendStyle?.lines?.down;
          if (trendStyle?.visible === false) continue;
          ctx.strokeStyle = trendStyle?.color ?? (p1.trend === 1 ? "#22c55e" : "#ef4444");
          ctx.lineWidth = trendStyle?.width ?? 2;
          ctx.globalAlpha = trendStyle?.opacity ?? 1;
          ctx.beginPath();
          ctx.moveTo(x0, getY(p0.value));
          ctx.lineTo(x1, getY(p1.value));
          ctx.stroke();
        }
        ctx.restore();
      }
    }

    // Donchian Channels
    if (donchian && donchian.length >= 2) {
      const styles = donchianStyle?.lines;
      const dcVisible = donchian.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (dcVisible.length >= 2) {
        const drawBand = (pts: { x: number; y: number }[], color: string, lw: number) => {
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = lw;
          ctx.lineJoin = "round";
          ctx.beginPath();
          pts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          ctx.stroke();
          ctx.restore();
        };
        const toXY = (p: any, key: string) => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]),
        });
        const fill = donchianStyle?.fills?.background;
        if (fill?.visible) {
          ctx.save();
          ctx.globalAlpha = fill.opacity;
          ctx.fillStyle = fill.color;
          ctx.beginPath();
          dcVisible.forEach((p, i) => {
            const { x, y } = toXY(p, "upper");
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          [...dcVisible].reverse().forEach((p) => {
            const { x, y } = toXY(p, "lower");
            ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
        if (styles?.top?.visible !== false)
          drawBand(
            dcVisible.map((p) => toXY(p, "upper")),
            styles?.top?.color ?? "rgba(56,189,248,0.7)",
            styles?.top?.width ?? 1,
          );
        if (styles?.middle?.visible !== false)
          drawBand(
            dcVisible.map((p) => toXY(p, "middle")),
            styles?.middle?.color ?? "rgba(56,189,248,0.35)",
            styles?.middle?.width ?? 1,
          );
        if (styles?.bottom?.visible !== false)
          drawBand(
            dcVisible.map((p) => toXY(p, "lower")),
            styles?.bottom?.color ?? "rgba(56,189,248,0.7)",
            styles?.bottom?.width ?? 1,
          );
      }
    }

    // Parabolic SAR (dots)
    if (sar && sar.length >= 1) {
      const sarLineStyle = sarStyle?.lines?.main;
      const sarVisible =
        sarLineStyle?.visible === false
          ? []
          : sar.filter((p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10);
      const sarWidth = sarLineStyle?.width ?? 1;
      const dotR = Math.max(1.5, Math.min(3, drawWidth / windowSize / 2)) * (0.6 + sarWidth * 0.35);
      sarVisible.forEach((p) => {
        const x = drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth;
        const y = getY(p.value);
        ctx.save();
        ctx.fillStyle = sarLineStyle?.color ?? (p.trend === 1 ? "#22c55e" : "#ef4444");
        ctx.beginPath();
        ctx.arc(x, y, dotR, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
    }

    if (fractal && fractal.length >= 1) {
      const frStyle = fractalStyle ?? {
        up: { visible: true, color: "#86DA0E" },
        down: { visible: true, color: "#FF0000" },
      };
      const frVisible = fractal.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      const size = Math.max(4, Math.min(7, drawWidth / windowSize / 1.5));
      const gap = size * 0.6;
      frVisible.forEach((p) => {
        const x = drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth;
        if (frStyle.up.visible && p.up != null) {
          const y = getY(p.up) - gap;
          ctx.save();
          ctx.fillStyle = frStyle.up.color;
          ctx.beginPath();
          ctx.moveTo(x, y - size);
          ctx.lineTo(x - size * 0.75, y);
          ctx.lineTo(x + size * 0.75, y);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
        if (frStyle.down.visible && p.down != null) {
          const y = getY(p.down) + gap;
          ctx.save();
          ctx.fillStyle = frStyle.down.color;
          ctx.beginPath();
          ctx.moveTo(x, y + size);
          ctx.lineTo(x - size * 0.75, y);
          ctx.lineTo(x + size * 0.75, y);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
      });
    }

    if (chaosbands && chaosbands.length >= 2) {
      const cbVisible = chaosbands.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (cbVisible.length >= 2) {
        const st = chaosbandsStyle ?? {
          up: { visible: true, color: "#25E154", width: 1, opacity: 1 },
          down: { visible: true, color: "#FF6C58", width: 1, opacity: 1 },
          background: { visible: true, color: "#57A1D0", opacity: 0.15 },
        };
        const toXY = (p: { time: number; upper?: number; lower?: number }, key: "upper" | "lower") => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]!),
        });
        const bothVisible = cbVisible.filter((p) => p.upper != null && p.lower != null);
        const upperPts = bothVisible.map((p) => toXY(p, "upper"));
        const lowerPts = bothVisible.map((p) => toXY(p, "lower"));
        const upperLinePts = cbVisible.filter((p) => p.upper != null).map((p) => toXY(p, "upper"));
        const lowerLinePts = cbVisible.filter((p) => p.lower != null).map((p) => toXY(p, "lower"));

        const strokeBand = (pts: { x: number; y: number }[]) => {
          ctx.beginPath();
          ctx.moveTo(pts[0].x, pts[0].y);
          for (let i = 1; i < pts.length; i++) {
            ctx.lineTo(pts[i].x, pts[i].y);
          }
          ctx.stroke();
        };

        if (st.background?.visible && upperPts.length >= 2) {
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(upperPts[0].x, upperPts[0].y);
          for (let i = 1; i < upperPts.length; i++) {
            ctx.lineTo(upperPts[i].x, upperPts[i].y);
          }
          for (let i = lowerPts.length - 1; i >= 0; i--) {
            ctx.lineTo(lowerPts[i].x, lowerPts[i].y);
          }
          ctx.closePath();
          ctx.fillStyle = colorWithOpacity(st.background.color, st.background.opacity);
          ctx.fill();
          ctx.restore();
        }

        if (st.up.visible && upperLinePts.length >= 2) {
          ctx.save();
          ctx.strokeStyle = colorWithOpacity(st.up.color, st.up.opacity ?? 1);
          ctx.lineWidth = st.up.width ?? 1;
          ctx.lineJoin = "miter";
          ctx.lineCap = "butt";
          strokeBand(upperLinePts);
          ctx.restore();
        }
        if (st.down.visible && lowerLinePts.length >= 2) {
          ctx.save();
          ctx.strokeStyle = colorWithOpacity(st.down.color, st.down.opacity ?? 1);
          ctx.lineWidth = st.down.width ?? 1;
          ctx.lineJoin = "miter";
          ctx.lineCap = "butt";
          strokeBand(lowerLinePts);
          ctx.restore();
        }
      }
    }

    // Envelopes (upper / middle MA / lower)
    if (envelopes && envelopes.length >= 2) {
      const envVisible = envelopes.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (envVisible.length >= 2) {
        const toXY = (p: any, key: string) => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]),
        });
        const st = envelopesStyle;
        const top = st?.top ?? { visible: true, color: "#F1C40F", width: 1, opacity: 0.95 };
        const mid = st?.middle ?? { visible: true, color: "#00FFCC", width: 1, opacity: 0.55 };
        const bot = st?.bottom ?? { visible: true, color: "#86DA0E", width: 1, opacity: 0.95 };
        const bg = st?.background;

        const upperPts = envVisible.map((p) => toXY(p, "upper"));
        const lowerPts = envVisible.map((p) => toXY(p, "lower"));

        if (bg?.visible) {
          ctx.save();
          ctx.beginPath();
          upperPts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          for (let i = lowerPts.length - 1; i >= 0; i--) ctx.lineTo(lowerPts[i].x, lowerPts[i].y);
          ctx.closePath();
          ctx.fillStyle = colorWithOpacity(bg.color, bg.opacity);
          ctx.fill();
          ctx.restore();
        }

        const drawBand = (pts: { x: number; y: number }[], ls: typeof top, dash: number[] = []) => {
          if (!ls.visible) return;
          ctx.save();
          ctx.strokeStyle = colorWithOpacity(ls.color, ls.opacity);
          ctx.lineWidth = ls.width;
          ctx.setLineDash(dash);
          ctx.lineJoin = "round";
          ctx.lineCap = "round";
          ctx.beginPath();
          pts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          ctx.stroke();
          ctx.restore();
        };
        drawBand(upperPts, top);
        drawBand(
          envVisible.map((p) => toXY(p, "middle")),
          mid,
          [4, 4],
        );
        drawBand(lowerPts, bot);
      }
    }

    // Keltner Channel (3 lines + fill between upper/lower)
    if (keltner && keltner.length >= 2) {
      const kcVisible = keltner.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (kcVisible.length >= 2) {
        const st = keltnerStyle;
        const top = st?.top ?? { visible: true, color: "#00FFCC", width: 1, opacity: 0.8 };
        const mid = st?.middle ?? { visible: true, color: "#00FFCC", width: 1, opacity: 0.5 };
        const bot = st?.bottom ?? { visible: true, color: "#00FFCC", width: 1, opacity: 0.8 };
        const bg = st?.background;
        const toXY = (p: any, key: string) => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]),
        });
        const upperPts = kcVisible.map((p) => toXY(p, "upper"));
        const lowerPts = kcVisible.map((p) => toXY(p, "lower"));

        if (bg?.visible) {
          ctx.save();
          ctx.beginPath();
          upperPts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          for (let i = lowerPts.length - 1; i >= 0; i--) ctx.lineTo(lowerPts[i].x, lowerPts[i].y);
          ctx.closePath();
          ctx.fillStyle = colorWithOpacity(bg.color, bg.opacity);
          ctx.fill();
          ctx.restore();
        }

        const drawBand = (pts: { x: number; y: number }[], ls: typeof top, dash: number[]) => {
          if (!ls.visible) return;
          ctx.save();
          ctx.strokeStyle = colorWithOpacity(ls.color, ls.opacity);
          ctx.lineWidth = ls.width;
          ctx.setLineDash(dash);
          ctx.lineJoin = "round";
          ctx.lineCap = "round";
          ctx.beginPath();
          pts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          ctx.stroke();
          ctx.restore();
        };
        drawBand(upperPts, top, []);
        drawBand(
          kcVisible.map((p) => toXY(p, "middle")),
          mid,
          [4, 4],
        );
        drawBand(lowerPts, bot, []);
      }
    }

    if (alligator) {
      const st = alligatorStyle;
      const lips = st?.top ?? { visible: true, color: "#00FFCC", width: 1, opacity: 1 };
      const teeth = st?.middle ?? { visible: true, color: "#E67E22", width: 1, opacity: 1 };
      const jaws = st?.bottom ?? { visible: true, color: "#2256FF", width: 1, opacity: 1 };
      const drawAlligatorLine = (series: { time: number; value: number }[] | undefined, ls: typeof lips) => {
        if (!ls.visible || !series || series.length < 2) return;
        const visible = series.filter(
          (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
        );
        if (visible.length < 2) return;
        ctx.save();
        ctx.strokeStyle = colorWithOpacity(ls.color, ls.opacity);
        ctx.lineWidth = ls.width;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.beginPath();
        visible.forEach((p, i) => {
          const x = drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth;
          const y = getY(p.value);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.restore();
      };
      drawAlligatorLine(alligator.jaws, jaws);
      drawAlligatorLine(alligator.teeth, teeth);
      drawAlligatorLine(alligator.lips, lips);
    }

    // Draw MA lines
    maNow.forEach((maData, idx) => {
      const maLineStyle = overlayNow.maStyle?.lines?.[`period${idx + 1}`];
      if (maLineStyle?.visible === false) return;
      if (maData.length < 2) return;
      const maVisible = maData.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (maVisible.length < 2) return;
      ctx.save();
      ctx.strokeStyle = maLineStyle?.color ?? MA_COLORS[idx] ?? "#38bdf8";
      ctx.globalAlpha = maLineStyle?.opacity ?? 1;
      ctx.lineWidth = maLineStyle?.width ?? 1.5;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.beginPath();
      maVisible.forEach((p, i) => {
        const x = drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth;
        const y = getY(p.value);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.restore();
    });

    ctx.restore();

    // Store badge data for overlay canvas to draw on top of bet badges
    currentPriceBadgeRef.current = currentPriceBadgeData;

    // Draw price labels
    ctx.fillStyle = "#9ca3af";
    ctx.font = `${isMobile ? 10 : 12}px sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";

    const priceLabelX = width - rightPadding + 10;
    const priceLabelInterval = calculateNicePriceInterval(paddedPriceRange);
    const firstLabelPrice = Math.ceil(paddedMinPrice / priceLabelInterval) * priceLabelInterval;
    for (let price = firstLabelPrice; price <= paddedMinPrice + paddedPriceRange; price += priceLabelInterval) {
      const y = getY(price) - 4;
      ctx.fillText(formatPriceValue(price, pricePrecision), priceLabelX, y);
    }

    // Draw time labels
    // startTime aligned with candle coordinate origin (viewportStartTime)
    const startTime = viewportStartTime;
    const endTime = currentViewportTime;

    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#9ca3af";

    const showSeconds = windowSize <= 180;

    // Check if viewport is showing a different day to adjust label width for dates
    const currentViewportTimestamp = baseTimeRef.current + currentViewportTime * 1000;
    const viewportDate = new Date(currentViewportTimestamp);
    const now = new Date();
    const isDifferentDay = !isSameDayInTz(viewportDate, now, timezone);

    const labelWidth = isDifferentDay ? (showSeconds ? 120 : 100) : showSeconds ? 65 : 45;
    const maxLabels = Math.floor(baseChartWidth / labelWidth);
    const actualLabels = Math.max(3, Math.min(maxLabels, 10));

    const timeRange = endTime - startTime; // = windowSize * (1 - emptySpaceFraction)
    const rawInterval = Math.max(timeRange, windowSize) / (actualLabels - 1);
    const niceInterval = calculateNiceInterval(rawInterval, showSeconds);

    // Future extends by emptySpaceFraction * windowSize — exactly fills the empty zone
    const futureTimeRange = emptySpaceFraction * windowSize;
    const futureEndTime = endTime + futureTimeRange;

    const firstTick = Math.floor(startTime / niceInterval) * niceInterval;
    let lastLabelRightEdge = -Infinity;

    const lastCandle = data[data.length - 1];
    const lastCandleRealMs = lastCandle?.timestamp ?? baseTimeRef.current + endTime * 1000;

    for (let timeIndex = firstTick; timeIndex <= futureEndTime; timeIndex += niceInterval) {
      if (timeIndex < startTime - niceInterval) continue;

      // For candlestick chart: find the closest candle and use its real timestamp
      let timestamp: number;
      const closestCandleIndex = data.findIndex((d) => Math.abs(d.time - timeIndex) < 0.5);

      if (closestCandleIndex !== -1 && data[closestCandleIndex].timestamp) {
        // Use real timestamp from candle data
        timestamp = data[closestCandleIndex].timestamp;
      } else {
        // Future labels: extend from last candle's real timestamp using actual candle duration
        const stepsAhead = timeIndex - lastDataTime;
        timestamp = lastCandleRealMs + stepsAhead * (candleDurationSeconds ?? 1) * 1000;
      }

      const date = new Date(timestamp);
      const timeStr = formatTime(date, showSeconds, Date.now(), timeInterval === "1D", timezone);

      // Unified coordinate: same origin as candles — no split formula for past/future
      const x = ((timeIndex - viewportStartTime) / windowSize) * stableDrawWidth;

      if (x >= 0 && x <= width) {
        const textW = ctx.measureText(timeStr).width;
        const labelLeft = x - textW / 2;
        if (labelLeft > lastLabelRightEdge + 8) {
          ctx.fillText(timeStr, x, chartHeight + 8);
          lastLabelRightEdge = x + textW / 2;
        }
      }
    }
  }, [
    data,
    haData,
    renderMode,
    getVisibleRange,
    windowSize,
    isMobile,
    scrollOffset,
    currentViewportTimeProp,
    baseTimeRef,
    canvasRef,
    hoveredButton,
    timeInterval,
    candleDurationSeconds,
    yScaleFactor,
    pricePrecision,
    verticalBias,
  ]);

  const drawOverlay = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas || data.length === 0) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const newW = Math.round(rect.width * dpr);
    const newH = Math.round(rect.height * dpr);
    if (overlaySizeRef.current.w !== newW || overlaySizeRef.current.h !== newH) {
      canvas.width = newW;
      canvas.height = newH;
      overlaySizeRef.current = { w: newW, h: newH };
      ctx.scale(dpr, dpr);
    }

    const width = rect.width;
    const height = rect.height;

    const currentViewportTime = getViewportTime ? getViewportTime() : currentViewportTimeProp;

    const rawLastDataTimeOverlay = data.length > 0 ? data[data.length - 1].time : 0;
    // drawChart runs first each frame and maintains frozenLastDataTimeRef; mirror its selection.
    const effectiveScrollOffset = scrollOffsetRef?.current ?? scrollOffset;
    const lastDataTime =
      isDraggingRef?.current || effectiveScrollOffset > 0 ? frozenLastDataTimeRef.current : rawLastDataTimeOverlay;

    const rightPadding = isMobile ? 50 : 95;
    const maxEmptySpace = Math.round((width - rightPadding) * CHART_CONFIG.padding.emptySpaceFraction);
    const transitionDuration = 5;
    let emptySpace: number;

    if (effectiveScrollOffset === 0) {
      emptySpace = maxEmptySpace;
    } else {
      if (currentViewportTime >= lastDataTime) {
        emptySpace = maxEmptySpace;
      } else if (currentViewportTime >= lastDataTime - transitionDuration) {
        const distanceFromCurrent = lastDataTime - currentViewportTime;
        const progress = distanceFromCurrent / transitionDuration;
        emptySpace = maxEmptySpace * (1 - progress);
      } else {
        emptySpace = 0;
      }
    }

    const stableDrawWidth = width - rightPadding;
    const emptySpaceFraction = stableDrawWidth > 0 ? emptySpace / stableDrawWidth : 0;
    const overlayViewportStartTime = currentViewportTime - windowSize * (1 - emptySpaceFraction);

    // Single coordinate function matching the main chart exactly
    const getTimeX = (relativeTimeSecs: number) =>
      ((relativeTimeSecs - overlayViewportStartTime) / windowSize) * stableDrawWidth;

    const bottomPadding = isMobile ? 16 : 30;
    const chartHeight = height - bottomPadding;

    ctx.clearRect(0, 0, width, height);

    // Clip overlay to chart area so flag lines don't bleed into the X-axis strip
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, chartHeight);
    ctx.clip();

    if (betBadgesRef) betBadgesRef.current = [];
    const xDomainEnd = currentViewportTime;

    const flagAbsoluteTime = flagTimeRef.current;

    const x = getTimeX(flagAbsoluteTime);

    // Show flag whenever it's within the visible viewport bounds.
    // Guard against NaN/Infinity when futureTimeRange collapses to 0 while scrolled.
    if (x !== undefined && Number.isFinite(x) && x >= 0 && x <= width - rightPadding) {
      const totalSeconds = selectedTimePeriodSeconds || 0;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const countdownText = `${minutes}:${seconds.toString().padStart(2, "0")}`;

      // Draw white circle with countdown at the top
      drawSvgFlag(ctx, x, 1, countdownText, isMobile);

      // Draw vertical line below the circle
      const circleRadius = isMobile ? 16 : 20;
      const lineStartY = 1 + circleRadius * 2 + 8;
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(x, lineStartY);
      ctx.lineTo(x, chartHeight);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // Draw flags for active bets
    if (activeBets && activeBets.length > 0) {
      // Calculate price range for Y positioning
      const { startIndex, endIndex } = getVisibleRange();
      const visibleData = data.slice(startIndex, Math.min(data.length, endIndex + 20));

      if (visibleData.length > 0 && smoothedMinPriceRef.current !== null && smoothedPriceRangeRef.current !== null) {
        const _center2 = smoothedMinPriceRef.current + smoothedPriceRangeRef.current / 2;
        const paddedPriceRange = smoothedPriceRangeRef.current / yScaleFactor;
        const paddedMinPrice = _center2 - paddedPriceRange / 2;

        const getPriceY = (price: number) => {
          return chartHeight - ((price - paddedMinPrice) / paddedPriceRange) * chartHeight;
        };

        const lastDP = data.length > 0 ? data[data.length - 1] : null;
        // data[i].time is a sequential integer index (one per candle), so 1 unit = candleDurationSeconds.
        // Empirical calculation from timestamps breaks when there's a gap (e.g. tab was hidden).
        const msPerCandle = (candleDurationSeconds || 60) * 1000;
        const refIndex = lastDP ? lastDP.time : (currentViewportTimeProp ?? 0);
        const isAtCandleLiveEdge =
          lastDP && visibleData.length > 0 && visibleData[visibleData.length - 1]?.time === lastDP.time;

        const candleSpacing = stableDrawWidth / windowSize;
        const badgeGap = Math.min(32, candleSpacing * 0.4);

        const activeBetIds = new Set<string>();
        const activeBetCreatedAts = new Set<string>();

        activeBets.forEach((bet) => {
          if (!bet.createdAt || !bet.targetTime || !bet.betType) {
            return;
          }

          const betCreatedMs = new Date(bet.createdAt).getTime();
          const betTargetMs = new Date(bet.targetTime).getTime();
          // uiKey stays stable across the optimistic (tempId) → confirmed (real betId)
          // rename; betId doesn't. Keying the anchor cache by betId meant confirmation
          // discarded the anchor computed at click time and recomputed a fresh one —
          // by then the chart's live edge had moved forward, so the bet's start
          // position visibly jumped to wherever "now" had become.
          const betKey = bet.uiKey ?? bet.betId;
          activeBetIds.add(betKey);
          activeBetCreatedAts.add(bet.createdAt);

          const betPrice = typeof bet.entryPrice === "string" ? parseFloat(bet.entryPrice) : bet.entryPrice || 0;

          let anchor = betAnchorRef.current.get(betKey);
          if (!anchor) {
            // A bet belongs to the current (building) candle iff its creation timestamp falls
            // within that candle — i.e. betCreatedMs >= the candle's open timestamp.
            const isLiveBet = isAtCandleLiveEdge && lastDP != null && betCreatedMs >= lastDP.timestamp;
            // Use snap-to-candle (not interpolation) so restored bets align with their candle
            // center exactly as they did when placed live (where startTime = lastDP.time).
            const startTime = isLiveBet ? refIndex : snapBetToCandle(betCreatedMs, data, refIndex);
            let birthTime = betBirthTimesRef.current.get(bet.createdAt);
            if (birthTime === undefined) {
              birthTime = performance.now();
              betBirthTimesRef.current.set(bet.createdAt, birthTime);
            }
            anchor = {
              startTime,
              targetTime: startTime + (betTargetMs - betCreatedMs) / msPerCandle,
              isLive: isLiveBet,
              entryPrice: betPrice,
              birthTime,
            };
            // Only cache once the bet's candle is within loaded data. If the bet predates
            // all loaded candles, tsToChartTime extrapolates inaccurately — skip caching
            // so the position is recalculated each frame as loadMoreHistory brings the
            // candle into range.
            const betInDataRange = data.length === 0 || betCreatedMs >= data[0].timestamp;
            if (isLiveBet || betInDataRange) {
              betAnchorRef.current.set(betKey, anchor);
            }
          } else if (anchor.isLive && lastDP != null && betCreatedMs < lastDP.timestamp) {
            // The candle this bet opened in has now closed.
            // Flip isLive without recomputing startTime — recomputing via tsToChartTime
            // can interpolate to N+ratio which jumps the dot to the next candle edge.
            // anchor.startTime was already set to refIndex (= candle N open) at creation.
            anchor.isLive = false;
            betAnchorRef.current.set(betKey, anchor);
          }

          // Live bets track the current candle's live position each frame.
          // Historical bets use the frozen chart-time coords set at anchor creation.
          const createdRelativeTime = anchor.isLive ? refIndex : anchor.startTime;
          const liveAdjustedTargetTime = anchor.isLive
            ? refIndex + (betTargetMs - betCreatedMs) / msPerCandle
            : anchor.targetTime;

          const lineStartX = Math.max(0, getTimeX(createdRelativeTime));
          const targetX = getTimeX(liveAdjustedTargetTime);

          if (Number.isFinite(lineStartX) && Number.isFinite(targetX)) {
            const lineY = getPriceY(anchor.entryPrice);
            const isCopyTrade = !!bet.isCopyTradingTrade;

            const lineColor = bet.betType === "put" ? "#F31260" : "#17C964";

            // Calculate time remaining
            const now = getServerNowMs();
            const betTargetTimestamp = new Date(bet.targetTime!).getTime();
            const rawTimeRemainingMs = Math.max(0, betTargetTimestamp - now);
            const timeRemainingMs = clampToMonotonicRemainingMs(betKey, rawTimeRemainingMs);
            const timeRemainingSeconds = Math.ceil(timeRemainingMs / 1000);
            const mins = Math.floor(timeRemainingSeconds / 60);
            const secs = timeRemainingSeconds % 60;
            const timeText = `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;

            {
              // Write badge info to ref for HTML overlay rendering
              if (betBadgesRef) {
                const bgColor = bet.betType === "put" ? "#F31260" : "#17C964";
                const padH = isMobile ? 8 : 10;
                const padV = 4;
                const amountFontSize = isMobile ? 11 : 14;
                const timeFontSize = isMobile ? 9 : 11;
                const arrowHalf = isMobile ? 5 : 6;
                const arrowGap = 5;
                const lineGap = isMobile ? 2 : 3;
                ctx.save();
                ctx.font = `bold ${amountFontSize}px sans-serif`;
                const amountW = ctx.measureText(`$${bet.amount}`).width;
                ctx.font = `${timeFontSize}px sans-serif`;
                const timeW = ctx.measureText(timeText).width;
                ctx.restore();
                const line1ContentW = arrowHalf * 2 + arrowGap + amountW;
                const badgeW = Math.max(line1ContentW, timeW) + padH * 2;
                const copyLabelH = isCopyTrade ? (isMobile ? 14 : 16) : 0;
                const badgeH = padV * 2 + amountFontSize + lineGap + timeFontSize + copyLabelH;
                const badgeX = lineStartX - badgeGap - badgeW - 8;
                const badgeY = lineY - badgeH / 2;
                if (isFinite(badgeX) && isFinite(badgeY) && badgeX + badgeW >= 0 && badgeX <= width - rightPadding) {
                  betBadgesRef.current.push({
                    id: betKey,
                    createdAt: bet.createdAt,
                    x: badgeX,
                    y: badgeY,
                    w: badgeW,
                    h: badgeH,
                    bgColor,
                    isCall: bet.betType !== "put",
                    amount: `$${bet.amount}`,
                    timeText,
                    isCopyTrade,
                  });
                }
              }

              // Draw entry price badge on Y axis
              {
                const priceText = formatPriceValue(betPrice, pricePrecision);
                const fontSize = isMobile ? 10 : 12;
                const badgePadding = 4;
                ctx.save();
                ctx.font = `${fontSize}px sans-serif`;
                ctx.textAlign = "left";
                ctx.textBaseline = "middle";
                const textW = ctx.measureText(priceText).width;
                const badgeW = textW + badgePadding * 2;
                const badgeH = fontSize + badgePadding * 2;
                const badgeX = width - rightPadding + 2;
                const badgeY = lineY - badgeH / 2;
                ctx.fillStyle = lineColor;
                ctx.beginPath();
                ctxRoundRect(ctx, badgeX, badgeY, badgeW, badgeH, 3);
                ctx.fill();
                ctx.fillStyle = "#000000";
                ctx.fillText(priceText, badgeX + badgePadding, lineY);
                ctx.restore();
              }

              // Draw short dashed line (badgeGap px) to the left of lineStartX — badge connector
              ctx.save();
              ctx.strokeStyle = lineColor;
              ctx.lineWidth = 1;
              ctx.globalAlpha = 0.5;
              ctx.setLineDash([3, 3]);
              ctx.beginPath();
              ctx.moveTo(Math.max(0, lineStartX - badgeGap), lineY);
              ctx.lineTo(lineStartX, lineY);
              ctx.stroke();
              ctx.setLineDash([]);
              ctx.restore();

              // Draw horizontal line from start to target
              if (lineStartX < targetX) {
                ctx.save();
                ctx.strokeStyle = lineColor;
                ctx.lineWidth = 1;
                ctx.globalAlpha = 1;
                ctx.beginPath();
                ctx.moveTo(lineStartX, lineY);
                ctx.lineTo(targetX, lineY);
                ctx.stroke();
                ctx.restore();

                // White dots at each end of the solid line
                const dotRadius = isMobile ? 2.5 : 3;
                ctx.save();
                ctx.fillStyle = "#FFFFFF";
                ctx.globalAlpha = 1;
                ctx.beginPath();
                ctx.arc(lineStartX, lineY, dotRadius, 0, Math.PI * 2);
                ctx.fill();
                ctx.beginPath();
                ctx.arc(targetX, lineY, dotRadius, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
              }

              // Draw dashed line from target to right edge
              ctx.save();
              ctx.strokeStyle = lineColor;
              ctx.lineWidth = 1;
              ctx.globalAlpha = 0.5;
              ctx.setLineDash([4, 4]);
              ctx.beginPath();
              ctx.moveTo(targetX, lineY);
              ctx.lineTo(width - rightPadding, lineY);
              ctx.stroke();
              ctx.setLineDash([]);
              ctx.restore();
            }

            // Draw controller circle anchored to the bet's expiry.
            // Guard against NaN targetX (happens when emptySpace=0 during scroll-back)
            if (isFinite(targetX) && targetX >= 0 && targetX <= width - rightPadding) {
              const dropElapsed = performance.now() - anchor.birthTime;
              const dropProgress = Math.min(dropElapsed / 600, 1);
              const dropEased = dropProgress >= 1 ? 1 : easeOutBack(dropProgress);
              const flagAlpha = Math.min(1, dropProgress / 0.6);
              const flagCircleRadius = isMobile ? 16 : 20;
              const circleCenterY = 1 + flagCircleRadius + 4;

              ctx.save();
              ctx.globalAlpha = flagAlpha;
              ctx.translate(targetX, circleCenterY);
              ctx.scale(dropEased, dropEased);
              ctx.translate(-targetX, -circleCenterY);
              drawSvgFlag(ctx, targetX, 1, timeText, isMobile, true, isCopyTrade);
              ctx.restore();

              const flagLineStartY = 1 + flagCircleRadius * 2 + 8;
              ctx.save();
              ctx.globalAlpha = flagAlpha;
              if (isCopyTrade) {
                const grad = ctx.createLinearGradient(targetX, flagLineStartY, targetX, chartHeight);
                grad.addColorStop(0, "#F89A09");
                grad.addColorStop(1, "rgba(248,154,9,0.15)");
                ctx.strokeStyle = grad;
              } else {
                ctx.strokeStyle = "#FFFFFF";
                ctx.globalAlpha = flagAlpha * 0.5;
              }
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(targetX, flagLineStartY);
              ctx.lineTo(targetX, chartHeight);
              ctx.stroke();
              ctx.restore();
            }
          }
        });

        for (const key of betAnchorRef.current.keys()) {
          if (!activeBetIds.has(key)) betAnchorRef.current.delete(key);
        }
        for (const key of betBirthTimesRef.current.keys()) {
          if (!activeBetCreatedAts.has(key)) betBirthTimesRef.current.delete(key);
        }
      }
    }

    // Draw user-added lines
    if (drawnLines.length > 0) {
      const gridEndX = width - rightPadding;
      const { startIndex, endIndex } = getVisibleRange();
      // Use exact visible range (no +20) so price range matches drawChart
      const visibleData = data.slice(startIndex, endIndex);
      // Match the coordinate system used by drawChart (stableDrawWidth + shifted xDomainStart)
      // so that shape handles align with their visual positions on the candle chart.
      const stableDrawWidth = width - rightPadding;
      const emptySpaceFraction = stableDrawWidth > 0 ? emptySpace / stableDrawWidth : 0;
      const linesXDomainStart = currentViewportTime - windowSize * (1 - emptySpaceFraction);
      drawLines({
        ctx,
        drawnLines,
        visibleData,
        allData: data,
        chartType: "candlestick",
        chartHeight,
        baseChartWidth: stableDrawWidth,
        gridEndX,
        xDomainStart: linesXDomainStart,
        windowSize,
        yScaleFactor,
        pricePrecision,
        smoothedMinPrice: smoothedMinPriceRef.current ?? undefined,
        smoothedPriceRange: smoothedPriceRangeRef.current ?? undefined,
      });
    }

    // Draw settled result clouds on canvas to avoid extra DOM/RAF pressure.
    const betResults = betResultRef?.current;
    if (betResults && betResults.length > 0 && data.length > 0) {
      const { startIndex, endIndex } = getVisibleRange();
      const visibleData = data.slice(startIndex, endIndex);
      if (visibleData.length > 0 && smoothedMinPriceRef.current !== null && smoothedPriceRangeRef.current !== null) {
        const _centerPill = smoothedMinPriceRef.current + smoothedPriceRangeRef.current / 2;
        const paddedPriceRangePill = smoothedPriceRangeRef.current / yScaleFactor;
        const paddedMinPricePill = _centerPill - paddedPriceRangePill / 2;

        for (const betResult of betResults) {
          // Last candle whose period contains closePriceTs
          let bestCandle = data[data.length - 1];
          for (const c of data) {
            if (c.timestamp <= betResult.closePriceTs) bestCandle = c;
            else break;
          }

          const pillX = getTimeX(bestCandle.time);
          const pillY = chartHeight - ((betResult.price - paddedMinPricePill) / paddedPriceRangePill) * chartHeight;

          if (Number.isFinite(pillX) && Number.isFinite(pillY)) {
            drawBetResultPill(ctx, pillX, pillY, betResult.type, betResult.amount);
          }
        }
      }
    }

    if (lastTickPositionRef?.current) {
      const { x: dotX, y: dotY } = lastTickPositionRef.current;
      if (isFinite(dotX) && isFinite(dotY) && dotX >= -20 && dotX <= width - rightPadding) {
        const pulseT = Date.now() / 1000;
        const pulseScale = 0.8 + Math.sin(pulseT * 3) * 0.2;
        const pulseOpacity = 0.6 + Math.sin(pulseT * 3) * 0.4;

        ctx.save();

        const glowRadius = 26 * pulseScale;
        const glow = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, glowRadius);
        glow.addColorStop(0, `rgba(255,255,255,${pulseOpacity * 0.8})`);
        glow.addColorStop(0.5, `rgba(255,255,255,${pulseOpacity * 0.25})`);
        glow.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(dotX, dotY, glowRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = 1;
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath();
        ctx.arc(dotX, dotY, 3, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      }
    }

    // Draw current price badge last — on top of all bet badges
    if (currentPriceBadgeRef.current && data.length > 0) {
      const { price, y, color } = currentPriceBadgeRef.current;
      const priceText = formatPriceValue(price, pricePrecision);
      const fontSize = isMobile ? 10 : 12;
      const badgePadding = 4;
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      const badgeWidth = ctx.measureText(priceText).width + badgePadding * 2;
      const badgeHeight = fontSize + badgePadding * 2;
      const badgeX = width - rightPadding + 2;
      const badgeY = y - badgeHeight / 2;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctxRoundRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, 3);
      ctx.fill();
      ctx.fillStyle = "#000000";
      ctx.fillText(priceText, badgeX + badgePadding, y);
    }

    // Restore clip so nothing else bleeds into the X-axis area
    ctx.restore();

    if (crosshairPosition) {
      drawCrosshair({
        ctx,
        position: crosshairPosition,
        canvasWidth: width,
        canvasHeight: height,
        chartOffsetX: 0,
        rightPadding,
        bottomPadding,
        isMobile,
        windowSize,
        timeInterval,
        timezone,
        pricePrecision,
      });
    }
  }, [
    data,
    windowSize,
    isMobile,
    scrollOffset,
    currentViewportTimeProp,
    flagTimeRef,
    overlayCanvasRef,
    crosshairPosition,
    baseTimeRef,
    hoveredButton,
    activeBets,
    getVisibleRange,
    drawnLines,
    yScaleFactor,
    pricePrecision,
    maDataSets,
    overlayIndicators,
    getViewportTime,
    scrollOffsetRef,
    renderMode,
    verticalBias,
    candleDurationSeconds,
    selectedTimePeriodSeconds,
    timezone,
    betBadgesRef,
    lastTickPositionRef,
    betResultRef,
    betResultRenderKey,
    isDraggingRef,
  ]);

  return { drawChart, drawOverlay };
};
